import React, { useState } from "react";
import styled from "styled-components";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Chip,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Skeleton from "@material-ui/lab/Skeleton";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import SearchIcon from "@material-ui/icons/Search";
import ListAltIcon from "@material-ui/icons/ListAlt";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { toaster } from "evergreen-ui";

const Page = styled(Box)`
    margin-top: 12px;
`;

const Header = styled(Paper)`
    position: relative;
    padding: 14px 16px;
    border-radius: 12px;
    background: #fafafa;
    overflow: hidden; /* lets the angled ribbon clip cleanly to the card */
`;

const HeaderRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
`;

const TitleGroup = styled(Box)`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Card = styled(Paper)`
    padding: 14px;
    border-radius: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border: 1px dashed #e0e0e0;
`;

const CardHeader = styled(Box)`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    & > .icon {
        margin-right: 8px;
    }
`;

const Actions = styled(Box)`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const TableWrap = styled(Paper)`
    margin-top: 12px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 1px dashed #e0e0e0;

    &::after {
        content: "PREVIEW • SKELETON";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-18deg);
        font-weight: 800;
        font-size: 48px;
        letter-spacing: 4px;
        color: rgba(0, 0, 0, 0.06);
        pointer-events: none;
        white-space: nowrap;
    }
`;

const Muted = styled.span`
    color: #5f6368;
`;

const ToggleLink = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    padding: 0;
    margin-top: 6px;
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    line-height: 1.4;
    display: inline-block;
    text-align: left;
    align-self: flex-start;
`;

const TooltipDark = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))`
    .MuiTooltip-tooltip {
        background: rgba(33, 33, 33, 1);
        color: #fff;
        font-size: 0.875rem;
        line-height: 1.25rem;
        padding: 8px 10px;
        border-radius: 8px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
        max-width: 360px;
    }
    .MuiTooltip-arrow {
        color: rgba(33, 33, 33, 1);
    }
`;

/* 45° corner ribbon aligned to the card design */
const CornerRibbon = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 140px;
    height: 140px;
    pointer-events: none;

    &::before {
        content: "PREVIEW";
        position: absolute;
        top: 18px;
        right: -42px; /* balances the diagonal across the rounded corner */
        transform: rotate(45deg);
        background: #1976d2;
        color: #fff;
        font-weight: 700;
        font-size: 12px;
        letter-spacing: 0.6px;
        padding: 6px 58px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
        border-radius: 4px;
    }

    @media (max-width: 600px) {
        width: 110px;
        height: 110px;

        &::before {
            top: 14px;
            right: -36px;
            font-size: 10px;
            padding: 4px 44px;
            border-radius: 3px;
        }
    }
`;

export default function BrokenLinks() {
    const [showMore, setShowMore] = useState(false);

    const notify = () => {
        toaster.success("Thanks! We’ll notify you when Broken Links goes live.");
    };

    return (
        <Page>
            <Alert severity="warning" icon={<InfoOutlinedIcon />}>
                <b>Preview only.</b> This page is a <i>skeleton</i> of the upcoming Broken Links feature.
                UI and data are examples and all actions are disabled.
            </Alert>

            <Header elevation={0}>
                <CornerRibbon aria-hidden="true" />

                <HeaderRow>
                    <TitleGroup>
                        <LinkOffIcon />
                        <Typography variant="h6">Broken Links</Typography>
                        <Chip size="small" color="secondary" label="Coming soon" />
                    </TitleGroup>
                </HeaderRow>

                <Box mt={1}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Find and track <b>404/410</b>, broken internal/external links, and missing assets — then
                        share tidy lists with your dev or content team.
                    </Typography>

                    {!showMore ? (
                        <ToggleLink onClick={() => setShowMore(true)}>Read more</ToggleLink>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Built for SEOs who live in <b>Excel</b>, <b>Screaming Frog</b>, and{" "}
                                <b>Search Console</b>. Upload a crawl or connect GSC and we’ll check at{" "}
                                <b>regular intervals</b> (daily / 2h / 4h) to show what’s fixed vs still broken —
                                perfect during <b>migrations</b> or large content updates.
                            </Typography>
                            <ToggleLink onClick={() => setShowMore(false)}>Show less</ToggleLink>
                        </>
                    )}
                </Box>
            </Header>

            <Box mt={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <div>
                                <CardHeader>
                                    <CloudUploadIcon className="icon" />
                                    <Typography variant="subtitle2">Upload a crawl (CSV)</Typography>
                                </CardHeader>
                                <Typography variant="body2" color="textSecondary">
                                    Import a <b>Screaming Frog</b> export and we’ll surface broken links by page,
                                    template, and status.
                                </Typography>
                            </div>
                            <Box mt={1}>
                                <Actions>
                                    <TooltipDark title="Preview only — disabled">
                    <span>
                      <Button variant="contained" color="primary" disabled>
                        Upload CSV
                      </Button>
                    </span>
                                    </TooltipDark>
                                    <Button onClick={notify}>Notify me</Button>
                                </Actions>
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <div>
                                <CardHeader>
                                    <SearchIcon className="icon" />
                                    <Typography variant="subtitle2">Connect Search Console</Typography>
                                </CardHeader>
                                <Typography variant="body2" color="textSecondary">
                                    Cross-check crawl issues with <b>GSC</b> coverage and soft-404 signals to
                                    prioritise fixes that matter.
                                </Typography>
                            </div>
                            <Box mt={1}>
                                <Actions>
                                    <TooltipDark title="Preview only — disabled">
                    <span>
                      <Button variant="contained" color="primary" disabled>
                        Connect GSC
                      </Button>
                    </span>
                                    </TooltipDark>
                                    <Button onClick={notify}>Notify me</Button>
                                </Actions>
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <div>
                                <CardHeader>
                                    <ListAltIcon className="icon" />
                                    <Typography variant="subtitle2">Paste URLs (quick check)</Typography>
                                </CardHeader>
                                <Typography variant="body2" color="textSecondary">
                                    Drop a list from Excel; we’ll probe response codes and flag broken outbound links
                                    fast.
                                </Typography>
                            </div>
                            <Box mt={1}>
                                <Actions>
                                    <TooltipDark title="Preview only — disabled">
                    <span>
                      <Button variant="contained" color="primary" disabled>
                        Paste list
                      </Button>
                    </span>
                                    </TooltipDark>
                                    <Button onClick={notify}>Notify me</Button>
                                </Actions>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <TableWrap variant="outlined">
                <Box p={2} display="flex" alignItems="center">
                    <InfoOutlinedIcon style={{ marginRight: 8 }} />
                    <Typography variant="subtitle2">Preview</Typography>
                    <Typography variant="body2" style={{ marginLeft: 8 }} color="textSecondary">
                        Example of the layout once live (data below is placeholder).
                    </Typography>
                </Box>
                <Divider />
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>URL</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Broken links on page</TableCell>
                            <TableCell>Referrers</TableCell>
                            <TableCell>Last seen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    <Skeleton width="80%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton width={60} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton width={40} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton width="50%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton width={80} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Box p={2} pt={0}>
                    <Typography variant="body2" color="textSecondary">
                        You’ll be able to filter by <b>status</b> (404, 410, soft-404, 5xx), group by{" "}
                        <b>template</b> or <b>directory</b>, and export to <b>CSV</b>. <Muted>Perfect for QA during migrations.</Muted>
                    </Typography>
                </Box>
            </TableWrap>
        </Page>
    );
}
